export const GLOBAL_PROJECTION_KEY = Symbol('global')

export const TYPES = {
  READ: 'r',
  WRITE: 'w',
  DELETE: 'd',
  CREATE: 'c',
  ACTION: 'a',
}

export const RESOURCES = {
  CONTRACT: 'contracts',
  DISPLAY: 'displays',
  ORGANIZATION: 'organizations',
  PLATFORM: 'platforms',
  PRODUCT: 'products',
  ROLE: 'roles',
  SCENE: 'scenes',
  TAG: 'tags',
  USER: 'users',
  CATEGORY: 'categories',
  ORDER: 'orders',
  OFFER: 'offers',
  OFFER_TYPE: 'offerTypes',
  JOB: 'jobs',
  JOB_TYPE: 'jobTypes',
  TASK: 'tasks',
  TASK_TYPE: 'taskTypes',
  STAGE_TEMPLATES: 'stageTemplates',
  LAYERS: 'layers',
  VISUALS: 'visuals',
  VISUAL_CLASSES: 'visualClasses',
  VIEWS: 'views',
  FEATURES: 'features',
  CUSTOM_TAGS: 'customTags',
  PROJECTS: 'projects',
  PROJECT_FOLLOWERS: 'projectFollowers',
  PACKAGE_EXPECTATIONS: 'packageExpectations',
}

export const regex = new RegExp(
  `^(${Object.values(TYPES).join('|')})_((${Object.values(RESOURCES).join('|')})+)(_([a-zA-Z0-9]+))?([+|-])?$`,
)

function convertIndexedGroupsToNamedGroups(permission, indexedGroups) {
  const indexes = {
    type: 1,
    resource: 2,
    attribute: 5,
    sign: 6,
  }
  let converted = {}
  for (const property in indexes) {
    converted[property] = indexedGroups[indexes[property]]
  }
  return converted
}

function parsePermissionWithValidation(permission, validatePermission) {
  const indexedGroups = regex.exec(permission)

  if (!indexedGroups || indexedGroups.length !== 7) {
    if (validatePermission) {
      throw new Error(`Invalid permission: "${permission}". It must match: "${regex}"`)
    } else {
      return null
    }
  }

  const groups = convertIndexedGroupsToNamedGroups(permission, indexedGroups)
  const { type, resource, attribute, sign } = groups
  return { type, resource, attribute, sign }
}

export function parsePermission(permission) {
  return parsePermissionWithValidation(permission, true)
}

export function generateMergedProjectionsFromPermissions(permissions, type, resource) {
  const permissionsParsed = permissions.map(p => parsePermissionWithValidation(p, false)).filter(v => !!v)
  let permissionsFilteredForPermission = permissionsParsed.filter(
    ({ resource: permissionResource, type: permissionType }) =>
      resource === permissionResource && type === permissionType,
  )
  const projections = {}

  for (const { sign, attribute } of permissionsFilteredForPermission) {
    projections[attribute || GLOBAL_PROJECTION_KEY] = sign === '+' ? 1 : -1
  }

  if (!projections[GLOBAL_PROJECTION_KEY] && Object.entries(projections).length === 0) {
    projections[GLOBAL_PROJECTION_KEY] = -1
  }

  return { ...projections }
}

function hasActionPermission(permissionsContext, permission, userPermissions, organizationId) {
  if (organizationId) {
    const permissions = getOrganizationPermissions(permissionsContext)
    return userPermissions.includes(permission) && permissions.includes(permission)
  }

  return userPermissions.includes(permission)
}

// TODO: Put in common with Backend permissions module
export function hasPermission(permissionsContext, expression, userPermissions, organizationId) {
  const permission = `${expression}+`

  if (permission.startsWith(TYPES.ACTION)) {
    return hasActionPermission(permissionsContext, permission, userPermissions, organizationId)
  }

  const projections = generateMergedProjections(permissionsContext, permission, userPermissions, organizationId)

  const { attribute } = parsePermission(permission)

  if (!attribute) {
    return projections[GLOBAL_PROJECTION_KEY] === 1 || hasAnyProjection(projections)
  }

  return (
    (projections[GLOBAL_PROJECTION_KEY] === 1 && projections[attribute] !== -1) ||
    (projections[attribute] === 1 && projections[GLOBAL_PROJECTION_KEY] !== -1)
  )
}

function generateMergedProjections(permissionsContext, permission, userPermissions, organizationId) {
  const { type, resource } = parsePermission(permission)

  const userProjections = generateMergedProjectionsFromPermissions(userPermissions, type, resource)

  if (organizationId === undefined) {
    return userProjections
  }

  const organizationPermissions = getOrganizationPermissions(permissionsContext)
  const organizationProjections = generateMergedProjectionsFromPermissions(organizationPermissions, type, resource)

  const projections = mergeProjections(userProjections, organizationProjections)
  return projections
}

function getOrganizationPermissions(permissionsContext) {
  if (!permissionsContext) throw new Error('missing permissionsContext')
  const organizationPermissions = new Set()
  for (const { permissions } of permissionsContext) {
    permissions.forEach(organizationPermissions.add, organizationPermissions)
  }

  return [...organizationPermissions]
}

function mergeProjections(userProjections, organizationProjections) {
  const isProjectionGloballyDenied =
    userProjections[GLOBAL_PROJECTION_KEY] === -1 || organizationProjections[GLOBAL_PROJECTION_KEY] === -1
  if (isProjectionGloballyDenied) {
    return { [GLOBAL_PROJECTION_KEY]: -1 }
  }

  if (!userProjections[GLOBAL_PROJECTION_KEY] && !hasAnyProjection(userProjections)) {
    return { [GLOBAL_PROJECTION_KEY]: -1 }
  }

  if (!organizationProjections[GLOBAL_PROJECTION_KEY]) {
    return fillProjection(organizationProjections, userProjections)
  }
  return fillProjection(userProjections, organizationProjections)
}

function hasAnyProjection(projections) {
  const projectionsClone = { ...projections }
  for (const key in DEFAULT_PROJECTIONS) {
    delete projectionsClone[key]
  }
  return Object.keys(projectionsClone).filter(key => projectionsClone[key] === 1).length > 0
}

function fillProjection(base, filter) {
  const projections = { ...base }
  for (const attribute in filter) {
    if (filter[attribute] === -1) {
      projections[attribute] = -1
    }
  }
  return projections
}

const DEFAULT_PROJECTIONS = { id: 1 }
